import CourtFinancials from './courtfinancials';
// src/components/account/FamilyJewelsDashboardPage.tsx

import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Divider, Spin, Alert, Typography } from 'antd';
import { DollarCircleOutlined, DownCircleOutlined, UpCircleOutlined, ProfileOutlined, MoneyCollectOutlined, UsergroupAddOutlined, SafetyOutlined } from '@ant-design/icons';
import { useGlobalContext } from '../../store/GlobalContext';

import { Asset, CashFlow, Liability, User } from '../../types';
import { getRecentAssets } from '../assets/asset.service';
import { getRecentCashflows } from '../cashflows/cashflow.service';
import { getRecentParticipants } from '../participant/participantService';
import { SummaryData, getSummary } from './summaryService';

const { Text } = Typography;

const FamilyJewelsDashboardPage: React.FC = () => {
  const { selectedEstate } = useGlobalContext();
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [cashflows, setCashflows] = useState<CashFlow[]>([]);
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [participants, setParticipants] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const estateId = selectedEstate?.uuid || '';

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!estateId) {
        if (isMounted) {
          setError('No estate selected.');
          setLoading(false);
        }
        return;
      }

      try {
        // Fetch all data concurrently
        const [summaryData, recentCashflows, recentAssets, ] = await Promise.all([
          getSummary(estateId),
          getRecentCashflows(estateId, 5),
          // getRecentLiabilities(estateId, 5),
          getRecentAssets(estateId, 5),
          // getRecentParticipants(estateId, 5),
        ]);

        if (isMounted) {
          setSummary(summaryData);
          setCashflows(recentCashflows);
          // setLiabilities(recentLiabilities);
          setAssets(recentAssets);
          // setParticipants(recentParticipants);
        }
      } catch (err: any) {
        console.error(err);
        if (isMounted) {
          setError(err.message || 'Failed to fetch dashboard data.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [estateId]);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin tip="Loading dashboard..." size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  const exemption = summary?.exemption;

  return (
    <div style={{ padding: '24px' }}>
      {/* Exempt-property notices. Shown above the figures so the reason a
          number looks the way it does arrives before the number itself. */}
      {exemption?.notices?.length ? (
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24}>
            {exemption.notices.map((notice) => (
              <Alert
                key={notice.code}
                type={notice.level === 'warning' ? 'warning' : 'info'}
                showIcon
                style={{ marginBottom: '8px' }}
                message={notice.message}
              />
            ))}
          </Col>
        </Row>
      ) : null}

      <Row gutter={[16, 16]}>
        {/* Summary Cards */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><DollarCircleOutlined style={{ marginRight: '8px' }} />Gross Estate Value</span>}
              value={summary?.estateValue}
              prefix={<DollarCircleOutlined />}
              precision={2}
              formatter={(value) => `${value.toLocaleString()}`}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Every asset, including those already distributed
            </Text>
          </Card>
        </Col>

        {/* What is actually left. The gross is the estate as it stood, so it
            does not move when an item is handed out; this does. Shown only once
            something has left, so an estate where nothing has is not cluttered
            with a card repeating the gross. */}
        {(summary?.distributedEstateValue ?? 0) > 0 && (
          <>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card className="dashboard-card">
                <Statistic
                  title={
                    <span>
                      <DollarCircleOutlined style={{ marginRight: '8px' }} />
                      Remaining Estate Value
                    </span>
                  }
                  value={summary?.remainingEstateValue}
                  prefix="$"
                  precision={2}
                  formatter={(value) => `${value.toLocaleString()}`}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Still in the estate, not yet distributed
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Card className="dashboard-card">
                <Statistic
                  title={
                    <span>
                      <DollarCircleOutlined style={{ marginRight: '8px' }} />
                      Distributed and Closed
                    </span>
                  }
                  value={summary?.distributedEstateValue}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#3f8600' }}
                  formatter={(value) => `${value.toLocaleString()}`}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Distributed, sold, donated, given away or thrown out
                </Text>
              </Card>
            </Col>
          </>
        )}

        {exemption?.applies && exemption.totalExempt > 0 && (
          <>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card className="dashboard-card">
                <Statistic
                  title={
                    <span>
                      <SafetyOutlined style={{ marginRight: '8px' }} />
                      Exempt Property
                    </span>
                  }
                  value={exemption.totalExempt}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#3f8600' }}
                  formatter={(value) => `${value.toLocaleString()}`}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Set aside for the family under {exemption.statute}
                </Text>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Card className="dashboard-card">
                <Statistic
                  title={
                    <span>
                      <DollarCircleOutlined style={{ marginRight: '8px' }} />
                      Probate Estate Value
                    </span>
                  }
                  value={summary?.probateEstateValue}
                  prefix="$"
                  precision={2}
                  formatter={(value) => `${value.toLocaleString()}`}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Gross value less exempt property
                </Text>
              </Card>
            </Col>
          </>
        )}
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><MoneyCollectOutlined style={{ marginRight: '8px' }} />Total Assets</span>}
              value={summary?.totalAssets}
            />
          </Card>
        </Col>
        {/* <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><DownCircleOutlined style={{ marginRight: '8px' }} />Total Liabilities</span>}
              value={summary?.totalLiabilities}
            />
          </Card>
        </Col> */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><UpCircleOutlined style={{ marginRight: '8px' }} />Total Inbound Cashflow</span>}
              value={summary?.totalInboundCashflow}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              formatter={(value) => `${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><DownCircleOutlined style={{ marginRight: '8px' }} />Total Outbound Cashflow</span>}
              value={summary?.totalOutboundCashflow}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              formatter={(value) => `${value.toLocaleString()}`}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Recent Transactions Section */}
      <Row gutter={[16, 16]}>
        {/* Recent Cashflow Transactions */}
        <Col xs={24} lg={12}>
          <Card title="Recent Cashflow Transactions" className="dashboard-section-card">
            <Table
              dataSource={cashflows}
              pagination={false}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Type", dataIndex: "type", key: "type", render: (type: string) => (
                  <Tag color={type === "Inbound" ? "green" : "red"}>{type}</Tag>
                )},
                { title: "Amount", dataIndex: "amount", key: "amount", render: (amount: string) => (
                  <span>${parseFloat(amount).toLocaleString()}</span>
                ) },
                { title: "Date", dataIndex: "date", key: "date" },
              ]}
              rowKey={(record) => record.name + record.createdAt}
              bordered
              size="small"
            />
          </Card>
        </Col>

        {/* Recent Liabilities */}
        <Col xs={24} lg={12}>
          <Card title="Recent Liabilities" className="dashboard-section-card">
            <Table
              dataSource={liabilities}
              pagination={false}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Amount", dataIndex: "amount", key: "amount", render: (amount: string) => (
                  <span>${parseFloat(amount).toLocaleString()}</span>
                ) },
                { title: "Due Date", dataIndex: "dueDate", key: "dueDate" },
              ]}
              rowKey={(record) => record.name + record.createdAt}
              bordered
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <CourtFinancials estateId={estateId} />
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        {/* Recently Added Assets */}
        <Col xs={24} lg={12}>
          <Card title="Recently Added Assets" className="dashboard-section-card">
            <Table
              dataSource={assets}
              pagination={false}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Value", dataIndex: "marketValue", key: "marketValue", render: (value: string | number) => (
                  <span>${Number(value).toLocaleString()}</span>
                ) },
                { title: "Date Added", dataIndex: "createdAt", key: "createdAt", render: (date: string) => new Date(date).toLocaleDateString() },
              ]}
              rowKey={(record) => record.name + record.createdAt}
              bordered
              size="small"
            />
          </Card>
        </Col>

        {/* Recent Participants */}
        <Col xs={24} lg={12}>
          <Card title="Recent Participants" className="dashboard-section-card">
            <Table
              dataSource={participants}
              pagination={false}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Role", dataIndex: "role", key: "role" },
                { title: "Date Added", dataIndex: "dateAdded", key: "dateAdded" },
              ]}
              rowKey={(record) => record.firstName + record.createdOn}
              bordered
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        {/* Asset Profitability */}
        <Col xs={24} lg={12}>
          <Card title="Asset value since date of death" className="dashboard-section-card">
            <Table
              dataSource={summary?.assetValuationChanges || []}
              pagination={false}
              columns={[
                { title: "Asset Name", dataIndex: "name", key: "name" },
                { title: "Date of death", dataIndex: "dateOfDeathValue", key: "dateOfDeathValue", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                { title: "Current", dataIndex: "currentValue", key: "currentValue", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                { title: "Change ($)", dataIndex: "changeSinceDeath", key: "changeSinceDeath", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                { title: "Change (%)", dataIndex: "changeSinceDeathPercent", key: "changeSinceDeathPercent", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                { title: "At distribution", dataIndex: "distributionValue", key: "distributionValue", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                { title: "Distribution change ($)", dataIndex: "changeOnDistribution", key: "changeOnDistribution", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                { title: "Distribution change (%)", dataIndex: "changeOnDistributionPercent", key: "changeOnDistributionPercent", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
              ]}
              rowKey="id"
              bordered
              size="small"
            />
          </Card>
        </Col>

        {/* Liability Profitability */}
        <Col xs={24} lg={12}>
          <Card title="Liability balances" className="dashboard-section-card">
            <Table
              dataSource={summary?.liabilityBalances || []}
              pagination={false}
              columns={[
                { title: "Liability Name", dataIndex: "name", key: "name" },
                { title: "Date of death", dataIndex: "dateOfDeathBalance", key: "dateOfDeathBalance", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                { title: "Outstanding", dataIndex: "outstandingBalance", key: "outstandingBalance", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
                { title: "Change ($)", dataIndex: "changeSinceDeath", key: "changeSinceDeath", render: (val: number | null) => val == null ? "Not recorded" : val.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
              ]}
              rowKey="id"
              bordered
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FamilyJewelsDashboardPage;
